/**
 * DevExtreme (esm/__internal/ui/drop_down_editor/m_drop_down_list.js)
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 * Read about DevExtreme licensing here: https://js.devexpress.com/Licensing/
 */
import _extends from "@babel/runtime/helpers/esm/extends";
import eventsEngine from "../../../common/core/events/core/events_engine";
import { addNamespace } from "../../../common/core/events/utils";
import messageLocalization from "../../../common/core/localization/message";
import dataQuery from "../../../common/data/query";
import registerComponent from "../../../core/component_registrator";
import devices from "../../../core/devices";
import Guid from "../../../core/guid";
import $ from "../../../core/renderer";
import { ChildDefaultTemplate } from "../../../core/templates/child_default_template";
import { ensureDefined, grep, noop } from "../../../core/utils/common";
import { Deferred } from "../../../core/utils/deferred";
import { extend } from "../../../core/utils/extend";
import { each } from "../../../core/utils/iterator";
import { getOuterHeight } from "../../../core/utils/size";
import { isDefined, isObject, isWindow } from "../../../core/utils/type";
import { getWindow } from "../../../core/utils/window";
import DataExpressionMixin from "../../../ui/editor/ui.data_expression";
import errors from "../../../ui/widget/ui.errors";
import { getDataSourceOptions } from "../../data/data_converter/grouped";
import DropDownEditor from "../../ui/drop_down_editor/m_drop_down_editor";
import List from "../../ui/list/list.edit.search";
const window = getWindow();
const LIST_ITEM_SELECTOR = ".dx-list-item";
const LIST_ITEM_DATA_KEY = "dxListItemData";
const DROPDOWNLIST_POPUP_WRAPPER_CLASS = "dx-dropdownlist-popup-wrapper";
const SEARCH_EVENT = "input";
const SEARCH_MODES = ["startswith", "contains", "endwith", "notcontains"];
const useCompositionEvents = "android" !== devices.real().platform;
class DropDownList extends DropDownEditor {
  _supportedKeys() {
    const parentSupportedKeys = super._supportedKeys();
    return _extends({}, parentSupportedKeys, {
      tab(e) {
        if (this._allowSelectItemByTab()) {
          this._saveValueChangeEvent(e);
          const {
            focusedElement: focusedElement
          } = this._list.option();
          const $focusedItem = $(focusedElement);
          if ($focusedItem.length) {
            this._setSelectedElement($focusedItem);
          }
        }
        parentSupportedKeys.tab(e);
      },
      space: noop,
      home: noop,
      end: noop
    });
  }
  _allowSelectItemByTab() {
    const {
      opened: opened,
      applyValueMode: applyValueMode
    } = this.option();
    return opened && "instantly" === applyValueMode;
  }
  _setSelectedElement($element) {
    const value = this._valueGetter(this._list._getItemData($element));
    this._setValue(value);
  }
  _setValue(value) {
    this.option("value", value);
  }
  _getDefaultOptions() {
    return _extends({}, super._getDefaultOptions(), DataExpressionMixin._dataExpressionDefaultOptions(), {
      displayValue: void 0,
      searchEnabled: false,
      searchMode: "contains",
      searchTimeout: 500,
      minSearchLength: 0,
      searchExpr: null,
      valueChangeEvent: "input change keyup",
      selectedItem: null,
      noDataText: messageLocalization.format("dxCollectionWidget-noDataText"),
      encodeNoDataText: false,
      onSelectionChanged: null,
      onItemClick: noop,
      showDataBeforeSearch: false,
      grouped: false,
      groupTemplate: "group",
      popupPosition: {
        my: "left top",
        at: "left bottom",
        offset: {
          h: 0,
          v: 0
        },
        collision: "flip"
      },
      wrapItemText: false,
      useItemTextAsTitle: false
    });
  }
  _defaultOptionsRules() {
    return super._defaultOptionsRules().concat([{
      device: {
        platform: "ios"
      },
      options: {
        popupPosition: {
          offset: {
            v: -1
          }
        }
      }
    }, {
      device: {
        platform: "generic"
      },
      options: {
        buttonsLocation: "bottom center"
      }
    }]);
  }
  _setOptionsByReference() {
    super._setOptionsByReference();
    extend(this._optionsByReference, {
      value: true,
      selectedItem: true,
      displayValue: true
    });
  }
  _init() {
    super._init();
    this._initDataExpressions();
    this._initActions();
    this._setListDataSource();
    this._validateSearchMode();
    this._clearSelectedItem();
    this._initItems();
  }
  _setListFocusedElementOptionChange() {
    this._list._updateParentActiveDescendant = this._updateActiveDescendant.bind(this);
  }
  _initItems() {
    const {
      items: items
    } = this.option();
    if (items && !items.length && this._dataSource) {
      this.option().items = this._dataSource.items();
    }
  }
  _initActions() {
    this._initContentReadyAction();
    this._initSelectionChangedAction();
    this._initItemClickAction();
  }
  _initContentReadyAction() {
    this._contentReadyAction = this._createActionByOption("onContentReady", {
      excludeValidators: ["disabled", "readOnly"]
    });
  }
  _initSelectionChangedAction() {
    this._selectionChangedAction = this._createActionByOption("onSelectionChanged", {
      excludeValidators: ["disabled", "readOnly"]
    });
  }
  _initItemClickAction() {
    this._itemClickAction = this._createActionByOption("onItemClick");
  }
  _initTemplates() {
    super._initTemplates();
    this._templateManager.addDefaultTemplates({
      item: new ChildDefaultTemplate("item")
    });
  }
  _isEditable() {
    const {
      searchEnabled: searchEnabled
    } = this.option();
    return super._isEditable() || searchEnabled;
  }
  _saveFocusOnWidget() {
    var _this$_list;
    if (null !== (_this$_list = this._list) && void 0 !== _this$_list && _this$_list.initialOption("focusStateEnabled")) {
      this._focusInput();
    }
  }
  _fitIntoRange(value, start, end) {
    if (value > end) {
      return start;
    }
    if (value < start) {
      return end;
    }
    return value;
  }
  _items() {
    const items = this._getPlainItems(!this._list && this._dataSource.items());
    const availableItems = new dataQuery(items).filter("disabled", "<>", true).toArray();
    return availableItems;
  }
  _calcNextItem(step) {
    const items = this._items();
    const nextIndex = this._fitIntoRange(this._getSelectedIndex() + step, 0, items.length - 1);
    return items[nextIndex];
  }
  _getSelectedIndex() {
    const items = this._items();
    const selectedItem = this.option("selectedItem");
    let result = -1;
    each(items, (index, item) => {
      if (this._isValueEquals(item, selectedItem)) {
        result = index;
        return false;
      }
    });
    return result;
  }
  _createPopup() {
    super._createPopup();
    this._updateCustomBoundaryContainer();
    this._popup.$wrapper().addClass(this._popupWrapperClass());
    const $popupContent = this._popup.$content();
    eventsEngine.off($popupContent, "mouseup");
    eventsEngine.on($popupContent, "mouseup", this._saveFocusOnWidget.bind(this));
  }
  _updateCustomBoundaryContainer() {
    const customContainer = this.option("dropDownOptions.container");
    const $container = customContainer && $(customContainer);
    if ($container && $container.length && !isWindow($container.get(0))) {
      const $containerWithParents = [].slice.call($container.parents());
      $containerWithParents.unshift($container.get(0));
      each($containerWithParents, (i, parent) => {
        if (parent === $("body").get(0)) {
          return false;
        }
        if ("hidden" === window.getComputedStyle(parent).overflowY) {
          this._$customBoundaryContainer = $(parent);
          return false;
        }
      });
    }
  }
  _popupWrapperClass() {
    return "dx-dropdownlist-popup-wrapper";
  }
  _renderInputValue() {
    var _this = this;
    let {
      value: value,
      renderOnly: renderOnly
    } = arguments.length > 0 && void 0 !== arguments[0] ? arguments[0] : {};
    const currentValue = value ?? this._getCurrentValue();
    this._rejectValueLoading();
    if (renderOnly) {
      return super._renderInputValue(currentValue);
    }
    return this._loadInputValue(currentValue, function () {
      _this._setSelectedItem(...arguments);
    }).always(super._renderInputValue.bind(this, currentValue));
  }
  _loadInputValue(value, callback) {
    return this._loadItem(value).always(callback);
  }
  _getItemFromPlain(value, cache) {
    let plainItems;
    let selectedItem;
    if (cache && "object" !== typeof value) {
      if (!cache.itemByValue) {
        cache.itemByValue = {};
        plainItems = this._getPlainItems();
        plainItems.forEach(function (item) {
          cache.itemByValue[this._valueGetter(item)] = item;
        }, this);
      }
      selectedItem = cache.itemByValue[value];
    }
    if (!selectedItem) {
      plainItems = this._getPlainItems();
      selectedItem = grep(plainItems, item => this._isValueEquals(this._valueGetter(item), value))[0];
    }
    return selectedItem;
  }
  _resetInputText() {
    this._renderInputValue({
      renderOnly: true
    });
  }
  _loadItem(value, cache) {
    const selectedItem = this._getItemFromPlain(value, cache);
    return void 0 !== selectedItem ? Deferred().resolve(selectedItem).promise() : this._loadValue(value);
  }
  _getPlainItems(items) {
    let plainItems = [];
    const {
      grouped: grouped
    } = this.option();
    items = items || this.option("items") || this._dataSource.items() || [];
    for (let i = 0; i < items.length; i++) {
      var _items$i;
      if (grouped && null !== (_items$i = items[i]) && void 0 !== _items$i && _items$i.items) {
        plainItems = plainItems.concat(items[i].items);
      } else {
        plainItems.push(items[i]);
      }
    }
    return plainItems;
  }
  _updateActiveDescendant($target) {
    var _this$_list2;
    const opened = this.option("opened");
    const listFocusedItemId = null === (_this$_list2 = this._list) || void 0 === _this$_list2 ? void 0 : _this$_list2.getFocusedItemId();
    const isElementOnDom = $(`#${listFocusedItemId}`).length > 0;
    const activedescendant = opened && isElementOnDom && listFocusedItemId;
    this.setAria({
      activedescendant: activedescendant || null
    }, $target);
  }
  _setSelectedItem(item) {
    const displayValue = this._displayValue(item);
    this.option("selectedItem", ensureDefined(item, null));
    this.option("displayValue", displayValue);
  }
  _displayValue(item) {
    return this._displayGetter(item);
  }
  _refreshSelected() {
    const cache = {};
    this._listItemElements().each((_, itemElement) => {
      const $itemElement = $(itemElement);
      const itemValue = this._valueGetter($itemElement.data("dxListItemData"));
      const isItemSelected = this._isSelectedValue(itemValue, cache);
      if (isItemSelected) {
        this._list.selectItem($itemElement);
      } else {
        this._list.unselectItem($itemElement);
      }
    });
  }
  _popupShownHandler() {
    super._popupShownHandler();
    this._setFocusPolicy();
  }
  _setFocusPolicy() {
    if (!this.option("focusStateEnabled") || !this._list) {
      return;
    }
    this._list.option("focusedElement", null);
  }
  _isSelectedValue(value, cache) {
    return this._isValueEquals(value, this.option("value"));
  }
  _validateSearchMode() {
    const searchMode = this.option("searchMode");
    const normalizedSearchMode = searchMode.toLowerCase();
    if (!SEARCH_MODES.includes(normalizedSearchMode)) {
      throw errors.Error("E1019", searchMode);
    }
  }
  _clearSelectedItem() {
    this.option("selectedItem", null);
  }
  _processDataSourceChanging() {
    this._initDataController();
    this._setListOption("_dataController", this._dataController);
    this._setListDataSource();
    this._renderInputValue().fail(() => {
      if (this._isCustomValueAllowed()) {
        return;
      }
      this._clearSelectedItem();
    });
  }
  _isCustomValueAllowed() {
    return this.option("displayCustomValue");
  }
  clear() {
    super.clear();
    this._clearFilter();
    this._clearSelectedItem();
  }
  _listItemElements() {
    return this._$list ? this._$list.find(".dx-list-item") : $();
  }
  _popupConfig() {
    return _extends({}, super._popupConfig(), {
      templatesRenderAsynchronously: false,
      autoResizeEnabled: false,
      maxHeight: this._getMaxHeight.bind(this)
    });
  }
  _renderPopupContent() {
    super._renderPopupContent();
    this._renderList();
  }
  _getKeyboardListeners() {
    const canListHaveFocus = this._canListHaveFocus();
    if (!canListHaveFocus) {
      return super._getKeyboardListeners().concat([this._list]);
    }
    return super._getKeyboardListeners();
  }
  _renderList() {
    this._listId = `dx-${new Guid()._value}`;
    const $list = $("<div>").attr("id", this._listId).appendTo(this._popup.$content());
    this._$list = $list;
    this._list = this._createComponent($list, List, this._listConfig());
    this._refreshList();
    this._renderPreventBlurOnListClick();
    this._setListFocusedElementOptionChange();
  }
  _renderPreventBlurOnListClick() {
    const eventName = addNamespace("mousedown", "dxDropDownList");
    eventsEngine.off(this._$list, eventName);
    eventsEngine.on(this._$list, eventName, e => e.preventDefault());
  }
  _getControlsAria() {
    return this._list && this._listId;
  }
  _renderOpenedState() {
    super._renderOpenedState();
    this._list && this._updateActiveDescendant();
    this.setAria("owns", this._popup && this._popupContentId);
  }
  _getAriaHasPopup() {
    return "listbox";
  }
  _refreshList() {
    if (this._list && this._shouldRefreshDataSource()) {
      this._setListDataSource();
    }
  }
  _shouldRefreshDataSource() {
    const dataSourceProvided = !!this._list.option("dataSource");
    return dataSourceProvided !== this._needPassDataSourceToList();
  }
  _isDesktopDevice() {
    return "desktop" === devices.real().deviceType;
  }
  _listConfig() {
    const {
      noDataText: noDataText,
      grouped: grouped,
      wrapItemText: wrapItemText,
      itemTemplate: itemTemplate,
      groupTemplate: groupTemplate,
      hoverStateEnabled: hoverStateEnabled,
      focusStateEnabled: focusStateEnabled,
      encodeNoDataText: encodeNoDataText,
      useItemTextAsTitle: useItemTextAsTitle
    } = this.option();
    const options = {
      selectionMode: "single",
      _templates: this.option("_templates"),
      templateProvider: this.option("templateProvider"),
      noDataText: noDataText,
      encodeNoDataText: encodeNoDataText,
      grouped: grouped,
      wrapItemText: wrapItemText,
      useItemTextAsTitle: useItemTextAsTitle,
      onContentReady: this._listContentReadyHandler.bind(this),
      itemTemplate: itemTemplate,
      indicateLoading: false,
      keyExpr: this._getCollectionKeyExpr(),
      displayExpr: this._displayGetterExpr(),
      groupTemplate: groupTemplate,
      onItemClick: this._listItemClickAction.bind(this),
      dataSource: this._getDataSource(),
      _dataController: this._dataController,
      hoverStateEnabled: this._isDesktopDevice() ? hoverStateEnabled : false,
      focusStateEnabled: this._isDesktopDevice() ? focusStateEnabled : false,
      _onItemsRendered: () => {
        this._popup.repaint();
      }
    };
    if (!this._canListHaveFocus()) {
      options.tabIndex = null;
    }
    return options;
  }
  _canListHaveFocus() {
    return false;
  }
  _getDataSource() {
    return this._needPassDataSourceToList() ? this._dataSource : null;
  }
  _dataSourceOptions() {
    return {
      paginate: false
    };
  }
  _getSpecificDataSourceOption() {
    const {
      grouped: grouped
    } = this.option();
    const dataSource = this.option("dataSource");
    if (dataSource && grouped) {
      return getDataSourceOptions(dataSource);
    }
    return dataSource;
  }
  _dataSourceFromUrlLoadMode() {
    return "raw";
  }
  _listContentReadyHandler() {
    this._list = this._list || this._$list.dxList("instance");
    if (!this.option("deferRendering")) {
      this._refreshSelected();
    }
    this._updatePopupWidth();
    this._updateListDimensions();
    this._contentReadyAction();
  }
  _setListOption(optionName, value) {
    this._setWidgetOption("_list", arguments);
  }
  _listItemClickAction(e) {
    this._listItemClickHandler(e);
    this._itemClickAction(e);
  }
  _listItemClickHandler(e) {}
  _setListDataSource() {
    if (!this._list) {
      return;
    }
    this._setListOption("dataSource", this._getDataSource());
    if (!this._needPassDataSourceToList()) {
      this._setListOption("items", []);
    }
  }
  _needPassDataSourceToList() {
    const {
      showDataBeforeSearch: showDataBeforeSearch
    } = this.option();
    return showDataBeforeSearch || this._isMinSearchLengthExceeded();
  }
  _isMinSearchLengthExceeded() {
    return this._searchValue().toString().length >= this.option("minSearchLength");
  }
  _needClearFilter() {
    return this._canKeepDataSource() ? false : this._needPassDataSourceToList();
  }
  _canKeepDataSource() {
    const isMinSearchLengthExceeded = this._isMinSearchLengthExceeded();
    return this._dataController.isLoaded() && this.option("showDataBeforeSearch") && this.option("minSearchLength") && !isMinSearchLengthExceeded && !this._isLastMinSearchLengthExceeded;
  }
  _searchValue() {
    return this._input().val() || "";
  }
  _getSearchEvent() {
    return addNamespace("input", `${this.NAME}Search`);
  }
  _getCompositionStartEvent() {
    return addNamespace("compositionstart", `${this.NAME}CompositionStart`);
  }
  _getCompositionEndEvent() {
    return addNamespace("compositionend", `${this.NAME}CompositionEnd`);
  }
  _getSetFocusPolicyEvent() {
    return addNamespace("input", `${this.NAME}FocusPolicy`);
  }
  _renderEvents() {
    super._renderEvents();
    eventsEngine.on(this._input(), this._getSetFocusPolicyEvent(), () => {
      this._setFocusPolicy();
    });
    if (this._shouldRenderSearchEvent()) {
      eventsEngine.on(this._input(), this._getSearchEvent(), e => {
        this._searchHandler(e);
      });
      if (useCompositionEvents) {
        eventsEngine.on(this._input(), this._getCompositionStartEvent(), () => {
          this._isTextCompositionInProgress(true);
        });
        eventsEngine.on(this._input(), this._getCompositionEndEvent(), e => {
          this._isTextCompositionInProgress(void 0);
          this._searchHandler(e, this._searchValue());
        });
      }
    }
  }
  _shouldRenderSearchEvent() {
    return this.option("searchEnabled");
  }
  _refreshEvents() {
    eventsEngine.off(this._input(), this._getSearchEvent());
    eventsEngine.off(this._input(), this._getSetFocusPolicyEvent());
    if (useCompositionEvents) {
      eventsEngine.off(this._input(), this._getCompositionStartEvent());
      eventsEngine.off(this._input(), this._getCompositionEndEvent());
    }
    super._refreshEvents();
  }
  _isTextCompositionInProgress(value) {
    if (arguments.length) {
      this._isTextComposition = value;
    } else {
      return this._isTextComposition;
    }
  }
  _searchHandler(e, searchValue) {
    if (this._isTextCompositionInProgress()) {
      return;
    }
    if (!this._isMinSearchLengthExceeded()) {
      this._searchCanceled();
      return;
    }
    const {
      searchTimeout: searchTimeout
    } = this.option();
    if (searchTimeout) {
      this._clearSearchTimer();
      this._searchTimer = setTimeout(() => {
        this._searchDataSource(searchValue);
      }, searchTimeout);
    } else {
      this._searchDataSource(searchValue);
    }
  }
  _searchCanceled() {
    this._clearSearchTimer();
    if (this._needClearFilter()) {
      this._filterDataSource(null);
    }
    this._refreshList();
  }
  _searchDataSource() {
    let searchValue = arguments.length > 0 && void 0 !== arguments[0] ? arguments[0] : this._searchValue();
    this._filterDataSource(searchValue);
  }
  _filterDataSource(searchValue) {
    this._clearSearchTimer();
    const dataController = this._dataController;
    dataController.searchExpr(this.option("searchExpr") || this._displayGetterExpr());
    dataController.searchOperation(this.option("searchMode"));
    dataController.searchValue(searchValue);
    dataController.load().done(this._dataSourceFiltered.bind(this, searchValue));
  }
  _clearFilter() {
    const dataController = this._dataController;
    dataController.searchValue() && dataController.searchValue(null);
  }
  _dataSourceFiltered(searchValue) {
    this._isLastMinSearchLengthExceeded = this._isMinSearchLengthExceeded();
    this._refreshList();
    this._refreshPopupVisibility();
  }
  _shouldOpenPopup() {
    return this._hasItemsToShow();
  }
  _refreshPopupVisibility() {
    if (this.option("readOnly") || !this._searchValue()) {
      return;
    }
    const shouldOpenPopup = this._shouldOpenPopup();
    if (shouldOpenPopup && !this._isFocused()) {
      return;
    }
    this.option("opened", shouldOpenPopup);
    if (shouldOpenPopup) {
      this._updatePopupWidth();
      this._updateListDimensions();
    }
  }
  _dataSourceChangedHandler(newItems) {
    if (0 === this._dataController.pageIndex()) {
      this.option().items = newItems;
    } else {
      this.option().items = this.option().items.concat(newItems);
    }
  }
  _hasItemsToShow() {
    const dataController = this._dataController;
    const resultItems = dataController.items() || [];
    const resultAmount = resultItems.length;
    const isMinSearchLengthExceeded = this._needPassDataSourceToList();
    return !!(isMinSearchLengthExceeded && resultAmount);
  }
  _clearSearchTimer() {
    clearTimeout(this._searchTimer);
    delete this._searchTimer;
  }
  _popupShowingHandler() {
    this._updatePopupWidth();
    this._updateListDimensions();
  }
  _dimensionChanged() {
    super._dimensionChanged();
    this._updateListDimensions();
  }
  _needPopupRepaint() {
    const dataController = this._dataController;
    const currentPageIndex = dataController.pageIndex();
    const needRepaint = isDefined(this._pageIndex) && currentPageIndex <= this._pageIndex || dataController.isLastPage() && !this._list._scrollViewIsFull();
    this._pageIndex = currentPageIndex;
    return needRepaint;
  }
  _updateListDimensions() {
    if (!this._popup) {
      return;
    }
    if (this._needPopupRepaint()) {
      this._popup.repaint();
    }
    if (this._list) {
      this._list.updateDimensions();
    }
  }
  _getMaxHeight() {
    const $element = this.$element();
    const $customBoundaryContainer = this._$customBoundaryContainer;
    const offsetTop = $element.offset().top - ($customBoundaryContainer ? $customBoundaryContainer.offset().top : 0);
    const windowHeight = getOuterHeight(window);
    const containerHeight = $customBoundaryContainer ? Math.min(getOuterHeight($customBoundaryContainer), windowHeight) : windowHeight;
    const maxHeight = Math.max(offsetTop, containerHeight - offsetTop - getOuterHeight($element));
    return Math.min(.5 * containerHeight, maxHeight);
  }
  _clean() {
    if (this._list) {
      delete this._list;
    }
    delete this._isLastMinSearchLengthExceeded;
    super._clean();
  }
  _dispose() {
    this._clearSearchTimer();
    super._dispose();
  }
  _setCollectionWidgetOption() {
    this._setListOption.apply(this, arguments);
  }
  _setSubmitValue() {
    const value = this.option("value");
    const submitValue = this._shouldUseDisplayValue(value) ? this._displayGetter(value) : value;
    this._getSubmitElement().val(submitValue);
  }
  _shouldUseDisplayValue(value) {
    return "this" === this.option("valueExpr") && isObject(value);
  }
  _optionChanged(args) {
    this._dataExpressionOptionChanged(args);
    switch (args.name) {
      case "hoverStateEnabled":
      case "focusStateEnabled":
        this._isDesktopDevice() && this._setListOption(args.name, args.value);
        super._optionChanged(args);
        break;
      case "items":
        if (!this.option("dataSource")) {
          this._processDataSourceChanging();
        }
        break;
      case "dataSource":
        this._processDataSourceChanging();
        break;
      case "valueExpr":
        this._renderValue();
        this._setListOption("keyExpr", this._getCollectionKeyExpr());
        break;
      case "displayExpr":
        this._renderValue();
        this._setListOption("displayExpr", this._displayGetterExpr());
        break;
      case "searchMode":
        this._validateSearchMode();
        break;
      case "minSearchLength":
        this._refreshList();
        break;
      case "searchEnabled":
      case "showDataBeforeSearch":
      case "searchExpr":
        this._invalidate();
        break;
      case "onContentReady":
        this._initContentReadyAction();
        break;
      case "onSelectionChanged":
        this._initSelectionChangedAction();
        break;
      case "onItemClick":
        this._initItemClickAction();
        break;
      case "grouped":
      case "groupTemplate":
      case "wrapItemText":
      case "noDataText":
      case "encodeNoDataText":
      case "useItemTextAsTitle":
        this._setListOption(args.name);
        break;
      case "displayValue":
        this.option("text", args.value);
        break;
      case "itemTemplate":
      case "searchTimeout":
        break;
      case "selectedItem":
        if (args.previousValue !== args.value) {
          this._selectionChangedAction({
            selectedItem: args.value
          });
        }
        break;
      default:
        super._optionChanged(args);
    }
  }
}
DropDownList.include(DataExpressionMixin);
registerComponent("dxDropDownList", DropDownList);
export default DropDownList;